import { useEffect, useState } from 'react';
import {
    Input, Button, DatePicker, Select, Radio, InputNumber, Empty, Segmented, Checkbox, Tooltip, message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { Phone, Search, Clock, ChevronDown, ChevronRight, Pencil, Hash } from 'lucide-react';
import Note from "./Note";
import {
    FAIL_REASONS, OUTCOME_LABEL, SYSTEM_AGENT, appointmentsOf, callCenterApi, callsOf, careOf, fmtDuration, hasRecording, isComplaintTask,
    isMedicalTask, missingNote, surveyStateOf, upcomingCallbacks, useCallCenter, visiblePhotos,
    type CallLog, type Customer, type Outcome,
} from '../../CustomerDemo/callCenterStore';
import { meName, softphone } from '../../CustomerDemo/softphone';
import { RecordingPlayer } from '../../CustomerDemo/components/CallWidgets';
import { normalize } from '../../Social/socialStore';
import { CareJourney, CsatTab, DayHeader, GalleryTab, groupByDay } from './CustomerCare';

const HOTLINE = '1900 6868';

const tabCls = (active: boolean, color: string) =>
    `pb-2 text-sm transition-colors border-b-2 cursor-pointer flex items-center gap-1.5 ${active ? `font-semibold ${color}` : 'font-medium text-gray-500 hover:text-gray-800 border-transparent'}`;

const Count = ({ n, tone = 'bg-gray-100 text-gray-600' }: { n: number; tone?: string }) =>
    n ? <span className={`text-[10px] px-1.5 rounded-full font-semibold ${tone}`}>{n}</span> : null;

// ============================ Lịch sử cuộc gọi ============================

function CallNoteEditor({ c, onDone }: { c: CallLog; onDone: () => void }) {
    const [v, setV] = useState(c.note || '');
    return (
        <div className="space-y-2">
            <Input.TextArea autoFocus value={v} onChange={e => setV(e.target.value)} autoSize={{ minRows: 2, maxRows: 6 }} placeholder="Nội dung trao đổi trong cuộc gọi..." />
            <div className="flex justify-end gap-2">
                <Button size="small" onClick={onDone}>Huỷ</Button>
                <Button size="small" type="primary" disabled={c.outcome === 'answered' && !v.trim()} onClick={() => { callCenterApi.updateCallNote(c.id, v, meName()); message.success('Đã lưu nội dung cuộc gọi'); onDone(); }}>Lưu</Button>
            </div>
        </div>
    );
}

function CallActivity({ c, open, onToggle, customer, isManager }: { c: CallLog; open: boolean; onToggle: () => void; customer: Customer; isManager: boolean }) {
    const [editing, setEditing] = useState(false);
    const out = c.direction === 'out';
    const ok = c.outcome === 'answered';
    const needNote = missingNote(c);
    // Nội dung cuộc gọi là lời khai của người gọi: chỉ người gọi (hoặc quản lý) được sửa; cuộc nhỡ của tổng đài thì ai cũng bổ sung được
    const canEdit = c.agent === meName() || c.agent === SYSTEM_AGENT || isManager;
    return (
        <div className="flex gap-3">
            <div className="flex items-start gap-1 pt-1.5 shrink-0">
                <button type="button" onClick={onToggle} className="text-gray-400 hover:text-gray-600 mt-2.5 cursor-pointer">{open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}</button>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${ok ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-500'}`}><Phone size={15} /></div>
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2 cursor-pointer" onClick={onToggle}>
                    <div className="min-w-0">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 text-[15px] flex items-center gap-2 flex-wrap">
                            {out ? `${c.agent} gọi ra` : `${customer.name} gọi đến`}
                            <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${ok ? 'bg-emerald-50 text-emerald-600' : 'bg-orange-50 text-orange-600'}`}>{OUTCOME_LABEL[c.outcome]}{c.duration ? ` · ${fmtDuration(c.duration)}` : ''}</span>
                            {c.manual && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-500">Ngoài tổng đài</span>}
                            {needNote && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Thiếu nội dung</span>}
                        </div>
                        <div className="text-sm text-gray-500 mt-0.5 truncate">
                            {c.note ? c.note : c.failReason ? `Lý do: ${FAIL_REASONS.find(r => r.id === c.failReason)?.text}` : <i>Không có nội dung</i>}
                        </div>
                    </div>
                    <span className="text-gray-400 text-[13px] shrink-0">{dayjs(c.startedAt).format('HH:mm')}</span>
                </div>
                {open && (
                    <div className="bg-[#f8f9fa] dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 mb-2 space-y-3">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-8 text-sm">
                            <div className="flex items-start gap-2"><Hash className="text-gray-400 mt-0.5" size={15} /><div><div className="text-gray-500 text-xs">Mã cuộc gọi</div><div className="font-medium text-gray-800 dark:text-gray-100 break-all">{c.id}</div></div></div>
                            <div className="flex items-start gap-2"><Clock className="text-gray-400 mt-0.5" size={15} /><div><div className="text-gray-500 text-xs">Thời gian đàm thoại</div><div className="font-medium text-gray-800 dark:text-gray-100">{fmtDuration(c.duration)}</div></div></div>
                            <div className="flex items-start gap-2"><Phone className="text-gray-400 mt-0.5" size={15} /><div><div className="text-gray-500 text-xs">Cuộc gọi từ</div><div className="font-medium text-gray-800 dark:text-gray-100">{out ? (c.manual ? `${c.agent} (máy riêng)` : HOTLINE) : c.phone}</div></div></div>
                            <div className="flex items-start gap-2"><Phone className="text-gray-400 mt-0.5" size={15} /><div><div className="text-gray-500 text-xs">Gọi đến</div><div className="font-medium text-gray-800 dark:text-gray-100">{out ? c.phone : HOTLINE}</div></div></div>
                        </div>
                        {editing ? <CallNoteEditor c={c} onDone={() => setEditing(false)} /> : (
                            <div className="text-sm">
                                {c.note ? <div className="text-gray-700 dark:text-gray-200 whitespace-pre-line">{c.note}</div> : <div className="text-gray-400 italic">Chưa có nội dung trao đổi</div>}
                                {c.noteEditedAt && (
                                    <div className="text-[11px] text-gray-400 mt-1">
                                        {c.noteHistory?.length ? 'Đã sửa' : 'Bổ sung'} lúc {dayjs(c.noteEditedAt).format('HH:mm DD/MM')}{c.noteEditedBy ? ` bởi ${c.noteEditedBy}` : ''}
                                        {!!c.noteHistory?.length && (
                                            <Tooltip title={<div className="space-y-1.5">{c.noteHistory.map((h, i) => (
                                                <div key={i}><div className="opacity-70 text-[11px]">{i === 0 ? 'Bản gốc' : `Bản ${i + 1}`} · {h.by} · {dayjs(h.at).format('HH:mm DD/MM')}</div><div>{h.note}</div></div>
                                            ))}</div>}>
                                                <span className="ml-1 underline cursor-help">xem {c.noteHistory.length} bản trước</span>
                                            </Tooltip>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                        {hasRecording(c) ? <RecordingPlayer call={c} compact /> : <div className="text-xs text-gray-400">{c.manual ? 'Cuộc gọi ngoài tổng đài — không có ghi âm' : 'Không có ghi âm'}</div>}
                        {!editing && (
                            <div className="flex gap-2">
                                <Tooltip title={canEdit ? undefined : `Chỉ ${c.agent} (người gọi) hoặc quản lý được sửa`}>
                                    <Button size="small" icon={<Pencil size={12} />} disabled={!canEdit} onClick={() => setEditing(true)}>{c.note ? 'Sửa nội dung' : 'Bổ sung nội dung'}</Button>
                                </Tooltip>
                                {!customer.doNotCall && <Button size="small" icon={<Phone size={12} />} onClick={() => softphone.dial(c.phone)}>Gọi lại</Button>}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

/** Ghi nhận cuộc gọi thực hiện ngoài tổng đài (điện thoại cá nhân, Zalo call...) */
function ManualCallForm({ customer }: { customer: Customer }) {
    const [phone, setPhone] = useState(customer.phones[0]);
    const [direction, setDirection] = useState<'out' | 'in'>('out');
    const [outcome, setOutcome] = useState<Outcome>('answered');
    const [at, setAt] = useState<Dayjs>(dayjs());
    const [min, setMin] = useState<number | null>(1);
    const [sec, setSec] = useState<number | null>(0);
    const [reason, setReason] = useState<number | undefined>();
    const [text, setText] = useState('');
    const [callback, setCallback] = useState<Dayjs | null>(null);
    const answered = outcome === 'answered';
    const duration = (min || 0) * 60 + (sec || 0);
    const problems = [
        // Khách đã yêu cầu không gọi → không ghi nhận cuộc gọi ra (khách tự gọi đến thì vẫn ghi)
        direction === 'out' && customer.doNotCall && `Khách đang bị chặn gọi (${customer.doNotCall.reason}) — không gọi ra`,
        answered && duration <= 0 && 'Nhập thời lượng cuộc gọi',
        answered && !text.trim() && 'Cuộc gọi nghe máy phải có nội dung trao đổi',
        at.isAfter(dayjs()) && 'Thời điểm gọi không được ở tương lai',
        at.isBefore(dayjs(customer.createdAt).startOf('day')) && `Thời điểm gọi trước ngày tạo khách (${dayjs(customer.createdAt).format('DD/MM/YYYY')})`,
        callback && callback.isBefore(dayjs()) && 'Hẹn gọi lại phải ở tương lai',
    ].filter(Boolean) as string[];

    const reset = () => { setText(''); setReason(undefined); setCallback(null); setAt(dayjs()); setMin(1); setSec(0); setOutcome('answered'); };
    const save = () => {
        if (problems.length) { message.warning(problems[0]); return; }
        callCenterApi.logCall({
            phone, direction, outcome, duration: answered ? duration : 0, agent: meName(), note: text, startedAt: at.valueOf(),
            failReason: answered ? undefined : reason, callbackAt: callback ? callback.valueOf() : null, manual: true,
        });
        message.success(callback ? 'Đã ghi nhận cuộc gọi và hẹn gọi lại' : 'Đã ghi nhận cuộc gọi');
        reset();
    };

    return (
        <div className="border border-gray-200 dark:border-gray-700 rounded-xl mb-6 shadow-sm">
            <div className="px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 text-sm font-semibold text-[#594eb1] flex items-center gap-2">
                <Phone size={14} /> Ghi nhận cuộc gọi ngoài tổng đài
                <Tooltip title="Dùng khi gọi bằng điện thoại cá nhân / Zalo call. Cuộc gọi qua tổng đài được ghi tự động."><span className="text-gray-400 font-normal text-xs cursor-help">(?)</span></Tooltip>
            </div>
            <div className="p-4 space-y-3 text-sm">
                <div className="flex flex-wrap items-center gap-3">
                    <Radio.Group size="small" value={direction} onChange={e => setDirection(e.target.value)} optionType="button" buttonStyle="solid" options={[{ value: 'out', label: 'Gọi đi' }, { value: 'in', label: 'Gọi đến' }]} />
                    {customer.phones.length > 1 && <Select size="small" className="w-36" value={phone} onChange={setPhone} options={customer.phones.map(p => ({ value: p, label: p }))} />}
                    <Select size="small" className="w-44" value={outcome} onChange={setOutcome} options={(Object.keys(OUTCOME_LABEL) as Outcome[]).map(k => ({ value: k, label: OUTCOME_LABEL[k] }))} />
                    <DatePicker size="small" showTime={{ format: 'HH:mm' }} format="HH:mm DD/MM/YYYY" allowClear={false} value={at} onChange={v => v && setAt(v)}
                        disabledDate={d => d.isAfter(dayjs(), 'day') || d.isBefore(dayjs(customer.createdAt), 'day')} />
                </div>
                <div className="flex flex-wrap items-center gap-3">
                    {answered ? (
                        <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">Thời lượng
                            <InputNumber size="small" min={0} max={240} value={min} onChange={setMin} className="!w-16" /> phút
                            <InputNumber size="small" min={0} max={59} value={sec} onChange={setSec} className="!w-16" /> giây
                        </span>
                    ) : (
                        <Select size="small" allowClear className="min-w-[240px]" value={reason} onChange={setReason} placeholder="Lý do không thành công"
                            options={FAIL_REASONS.map(r => ({ value: r.id, label: r.text }))} />
                    )}
                    <DatePicker size="small" showTime={{ format: 'HH:mm' }} format="HH:mm DD/MM/YYYY" value={callback} onChange={setCallback} placeholder="Hẹn gọi lại (tuỳ chọn)"
                        disabledDate={d => d.isBefore(dayjs(), 'day')} />
                </div>
                <Input.TextArea value={text} onChange={e => setText(e.target.value)} autoSize={{ minRows: 3, maxRows: 8 }}
                    status={answered && !text.trim() ? 'warning' : undefined}
                    placeholder={answered ? 'Bắt buộc: khách quan tâm gì, đã tư vấn gì, bước tiếp theo...' : 'Ghi chú (tuỳ chọn)'} />
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <span className="text-xs text-amber-600">{problems[0] || ''}</span>
                    <span className="flex gap-2">
                        <Button onClick={reset}>Huỷ</Button>
                        <Button type="primary" disabled={problems.length > 0} onClick={save}>Lưu cuộc gọi</Button>
                    </span>
                </div>
            </div>
        </div>
    );
}

function CallHistory({ customer, isManager }: { customer: Customer; isManager: boolean }) {
    const s = useCallCenter();
    const [q, setQ] = useState('');
    const [direction, setDirection] = useState<'all' | 'out' | 'in'>('all');
    const [filter, setFilter] = useState<Outcome | 'all'>('all');
    const [onlyMissingNote, setOnlyMissingNote] = useState(false);
    const [openIds, setOpenIds] = useState<Set<string>>(new Set());
    const [expandAll, setExpandAll] = useState(false);

    const all = callsOf(s, customer.phones);
    const out = all.filter(c => c.direction === 'out');
    const stats = {
        total: all.length,
        out: out.length,
        // Tỉ lệ nghe máy chỉ tính cuộc gọi ra (khả năng tiếp cận khách)
        outAnswered: out.filter(c => c.outcome === 'answered').length,
        talk: all.reduce((t, c) => t + c.duration, 0),
        missingNote: all.filter(missingNote).length,
    };
    const kw = normalize(q);
    const calls = all.filter(c =>
        (direction === 'all' || c.direction === direction)
        && (filter === 'all' || c.outcome === filter)
        && (!onlyMissingNote || missingNote(c))
        && (!kw || normalize(`${c.note || ''} ${c.agent} ${c.phone}`).includes(kw)));

    const toggle = (id: string) => setOpenIds(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

    return (
        <div className="flex-1 flex flex-col min-h-0 pb-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                {[
                    ['Tổng cuộc gọi', `${stats.total} (${stats.out} gọi ra)`, 'text-gray-900 dark:text-gray-100'],
                    ['Tỉ lệ nghe máy (gọi ra)', stats.out ? `${Math.round((stats.outAnswered / stats.out) * 100)}%` : '—', 'text-emerald-600'],
                    ['Tổng đàm thoại', fmtDuration(stats.talk), 'text-gray-900 dark:text-gray-100'],
                    ['Thiếu nội dung', String(stats.missingNote), stats.missingNote ? 'text-amber-600' : 'text-gray-900 dark:text-gray-100'],
                ].map(([l, v, cls]) => (
                    <div key={l} className="border border-gray-200 dark:border-gray-700 rounded-xl p-3">
                        <div className="text-xs text-gray-500">{l}</div>
                        <div className={`text-lg font-bold ${cls}`}>{v}</div>
                    </div>
                ))}
            </div>

            <ManualCallForm customer={customer} />

            <div className="flex flex-wrap items-center justify-between mb-3 gap-3">
                <h3 className="text-[17px] font-bold text-gray-900 dark:text-gray-100">Cuộc gọi ({calls.length}{calls.length !== all.length ? `/${all.length}` : ''})</h3>
                <button type="button" onClick={() => { setExpandAll(v => !v); setOpenIds(new Set()); }} className="text-sm text-[#594eb1] hover:underline cursor-pointer">{expandAll ? 'Thu gọn tất cả' : 'Mở rộng tất cả'}</button>
            </div>
            <div className="flex flex-wrap items-center gap-3 mb-5">
                <Input allowClear className="!max-w-xs" prefix={<Search size={14} className="text-gray-400" />} placeholder="Tìm nội dung, nhân viên" value={q} onChange={e => setQ(e.target.value)} />
                <Segmented value={direction} onChange={v => setDirection(v as 'all' | 'out' | 'in')} options={[{ value: 'all', label: 'Tất cả' }, { value: 'out', label: 'Gọi đi' }, { value: 'in', label: 'Gọi đến' }]} />
                <Select className="w-44" value={filter} onChange={setFilter} options={[{ value: 'all', label: 'Mọi kết quả' }, ...(Object.keys(OUTCOME_LABEL) as Outcome[]).map(k => ({ value: k, label: OUTCOME_LABEL[k] }))]} />
                <Checkbox checked={onlyMissingNote} onChange={e => setOnlyMissingNote(e.target.checked)}>Chỉ cuộc thiếu nội dung</Checkbox>
            </div>
            {calls.length ? groupByDay(calls, c => c.startedAt).map(g => (
                <div key={g.day} className="mb-2">
                    <DayHeader label={g.day} />
                    <div className="space-y-4">
                        {g.items.map(c => {
                            const open = expandAll ? !openIds.has(c.id) : openIds.has(c.id);
                            return <CallActivity key={c.id} c={c} customer={customer} isManager={isManager} open={open} onToggle={() => toggle(c.id)} />;
                        })}
                    </div>
                </div>
            )) : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={all.length ? 'Không có cuộc gọi khớp bộ lọc' : 'Chưa có cuộc gọi'} />}
        </div>
    );
}

// ============================ Khung tab ============================

/** Yêu cầu mở thẳng một tab con (vd từ việc "Khảo sát" ở trang danh sách). n đổi mỗi lần để kích hoạt lại */
export interface CareFocus { tab: 'khao-sat'; appointmentId?: string; n: number }

export default function WorkHistoryPanel({ customer, onCall, isManager = false, focus }: { customer: Customer; onCall: () => void; isManager?: boolean; focus?: CareFocus | null }) {
    const s = useCallCenter();
    const [activeSubTab, setActiveSubTab] = useState<'ghi-chu' | 'cham-soc' | 'lich-su'>('cham-soc');
    const [activeCareTab, setActiveCareTab] = useState<'hanh-trinh' | 'khao-sat' | 'gallery'>('hanh-trinh');
    const [surveyAppt, setSurveyAppt] = useState<string>();
    const notes = careOf(s, customer.phones, 'note').length;
    const calls = callsOf(s, customer.phones);
    const missing = calls.filter(missingNote).length;
    const open = upcomingCallbacks(s, customer.phones);
    const urgent = open.filter(cb => isMedicalTask(cb) || isComplaintTask(cb)).length;
    const pendingSurveys = appointmentsOf(s, customer.id).filter(a => a.status === 'done' && surveyStateOf(s, a.id).state === 'pending').length;

    const openSurvey = (appointmentId?: string) => { setActiveSubTab('cham-soc'); setActiveCareTab('khao-sat'); setSurveyAppt(appointmentId); };
    useEffect(() => { if (focus?.tab === 'khao-sat') openSurvey(focus.appointmentId); }, [focus]);

    return (
        <div className="flex flex-col h-full">
            <div className="flex gap-6 border-b border-gray-100 dark:border-gray-800 mb-5 shrink-0 overflow-x-auto">
                <button type="button" onClick={() => setActiveSubTab('ghi-chu')} className={tabCls(activeSubTab === 'ghi-chu', 'text-[#40849c] border-[#40849c]')}>Ghi chú <Count n={notes} /></button>
                <button type="button" onClick={() => setActiveSubTab('cham-soc')} className={tabCls(activeSubTab === 'cham-soc', 'text-[#40849c] border-[#40849c]')}>
                    Chăm sóc khách hàng <Count n={open.length} tone={urgent ? 'bg-rose-500 text-white' : 'bg-amber-100 text-amber-700'} />
                </button>
                <button type="button" onClick={() => setActiveSubTab('lich-su')} className={tabCls(activeSubTab === 'lich-su', 'text-[#40849c] border-[#40849c]')}>
                    Lịch sử cuộc gọi <Count n={calls.length} />
                    {missing > 0 && <Tooltip title={`${missing} cuộc nghe máy chưa có nội dung`}><span className="w-2 h-2 rounded-full bg-amber-500" /></Tooltip>}
                </button>
            </div>

            {activeSubTab === 'ghi-chu' && <Note phones={customer.phones} />}

            {activeSubTab === 'cham-soc' && (
                <div className="flex-1 flex flex-col min-h-0">
                    <div className="flex gap-8 border-b border-gray-200 dark:border-gray-800 mb-5 shrink-0 px-2 overflow-x-auto">
                        <button type="button" onClick={() => setActiveCareTab('hanh-trinh')} className={tabCls(activeCareTab === 'hanh-trinh', 'text-[#0d9488] border-[#0d9488]')}>Hành trình (Timeline)</button>
                        <button type="button" onClick={() => openSurvey()} className={tabCls(activeCareTab === 'khao-sat', 'text-[#0d9488] border-[#0d9488]')}>Khảo sát CSAT <Count n={pendingSurveys} tone="bg-amber-100 text-amber-700" /></button>
                        <button type="button" onClick={() => setActiveCareTab('gallery')} className={tabCls(activeCareTab === 'gallery', 'text-[#0d9488] border-[#0d9488]')}>Hình ảnh <Count n={visiblePhotos(customer).length} /></button>
                    </div>
                    {activeCareTab === 'hanh-trinh' && <CareJourney customer={customer} onCall={onCall} onSurvey={openSurvey} />}
                    {activeCareTab === 'khao-sat' && <CsatTab customer={customer} focusAppointment={surveyAppt} />}
                    {activeCareTab === 'gallery' && <GalleryTab customer={customer} isManager={isManager} />}
                </div>
            )}

            {activeSubTab === 'lich-su' && <CallHistory customer={customer} isManager={isManager} />}
        </div>
    );
}
